<template>
  <div class="container">
    <div v-if="!samples">
    </div>
    <div v-else class="samples">
      <div v-for="(sample, index) in samples" :key="sample.id">
        <div class="sample-container" :class="{ 'card-selected': sample.selected }">
          <SampleCard :date="sample.date" :lab-name="sample.source_lab" :sample-id="sample.sample"
            :selected="sample.selected" :specimen-type="sample.tissue_type" :test-type="sample.test_performed"
            :class="{ 'item-selected': cardIndex === index }" @keyup.enter="onOpenGenomicsResults(sample, index)"
            @click.prevent="onOpenGenomicsResults(sample, index)">
          </SampleCard>
          <div v-if="sample.sample && cardWithSpecimenCount >= MIN_COMPARED" class="checkbox-layer">
            <span v-if="sample.selected" class="material-symbols-outlined size-24 checked"
              @click.prevent="onSelectSample(sample)">
              check_circle
            </span>
            <span v-else class="material-symbols-outlined size-24 unchecked" @click.prevent="onSelectSample(sample)">
              radio_button_unchecked
            </span>
          </div>
          <div v-else-if="!sample.sample" class="report-only">
            <span>{{ sample.mimetype.includes('pdf') ? 'PDF' : 'TXT' }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="comparison-section" v-show="cardWithSpecimenCount >= MIN_COMPARED">
      <span v-if="cardCount" class="genomics-disclaimer">
        {{ tr('The test results displayed are based on the' +
          ' data that was made available to the dātma system') }}
      </span>
      <div v-if="cardCount" class="genomics-compare-box">
        <button class="primary-outlined-button" @click.prevent="clearSelectedSamples" :disabled="!selectedCardCount"
          :class="{ 'button-disabled': !selectedCardCount }">{{ tr('Clear') }}</button>
        <button class="primary-filled-button" @click.prevent="onClickCompareButton"
          :disabled="selectedCardCount < MIN_COMPARED" :class="{ 'button-disabled': selectedCardCount < MIN_COMPARED }">{{
            tr('Compare') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, defineExpose, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { filterSamples, getSamples, getTrialsLink } from './getSpecimens.js'
import SampleCard from './SampleCard.vue'
import { clearCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'
import { getReportFile } from '@/ux3/cards/ReportViewer/getReportFile.js'
import { compareDatesDescending, showToast } from '@/utils/utils'

const MAX_COMPARED = 3
const MIN_COMPARED = 2

const store = useStore()
const samples = ref([])
const currentlySelectedSample = ref('')
const cardCount = ref(0)
const cardWithSpecimenCount = computed(() => samples.value.filter(sample => sample.sample).length)
const selectedCardCount = ref(0)
const trialLink = ref('')

const tr = (x) => x // inject('tr')
const cardIndex = ref(-1)

const emit = defineEmits(['onSpecimensCountUpdated', 'onCardsLoading', 'onCardsFinishedLoading'])

const patientId = computed(() => store.getters.session?.patient?.id || '')
const isCenterColumnGenomics = computed(() => store.getters.session?.centerColumnState.genomics || false)

const onOpenGenomicsResults = async (sample, index) => {
  cardIndex.value = index
  // This is the case for the PDF/txt/doc types that use the report viewer
  if (!sample.sample) {
    if (!sample.uuid || !sample.mimetype) { return }
    store.dispatch('showViewer', true)
    store.dispatch('reportDocType', sample.mimetype)
    store.dispatch('reportDocument', await getReportFile(sample.uuid, sample.mimetype))
    return
  }
  // we don't want to reload when we click the same sample card
  if (sample.sample === currentlySelectedSample.value && isCenterColumnGenomics.value) { return }
  const trialData = await getTrialsLink(sample.sample)

  // get the first link only for now
  trialLink.value = trialData?.length && trialData[0] || ''
  currentlySelectedSample.value = sample.sample
  const cardState = {
    accessionNum: sample.accession_number,
    sampleId: sample.sample,
    specimenType: sample.tissue_type,
    testType: sample.test_performed,

    // clearing values on a new sample , initially. Correctly set elsewhere
    // after loading genomics
    selectedAlteration: '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: 0,
    uuid: sample.uuid || '',
    mimeType: sample.mimetype || '',
    multiComparison: false,
    trialLink: trialLink.value,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
  clearCenterColumn('genomics')
}

const loadSamples = async (id) => {
  if (!id) { return }

  //presently, what we get back looks like: {results: Array(23)}
  emit('onCardsLoading')
  const results = await getSamples(id)
  if (!results.results) { return emit('onCardsFinishedLoading') }
  samples.value = []

  const filtered = await filterSamples(results.results, id)
  samples.value = filtered.sort(compareDatesDescending)

  clearSelectedSamples()
  emit('onCardsFinishedLoading')

  cardCount.value = samples.value.length

  emit('onSpecimensCountUpdated', samples.value.length)
}

const clearSelectedSamples = () => {
  //give all samples a default of unselected
  samples.value.forEach(sample => {
    sample.selected = false
  })
  selectedCardCount.value = 0
}

const onSelectSample = (sample) => {
  sample.selected = !sample.selected
  sample.selected ? selectedCardCount.value++ : selectedCardCount.value--
}

const onClickCompareButton = () => {
  if (!selectedCardCount.value) { return }
  cardIndex.value = -1
  if (selectedCardCount.value > MAX_COMPARED) {
    showToast('info', 'Only three files can be compared at a time')
    return
  }
  currentlySelectedSample.value = ''
  const selectedSamples = []
  const selectedAccessionNumbers = []
  samples.value.forEach(sample => {
    if (sample.selected) {
      selectedSamples.push(sample)
      selectedAccessionNumbers.push({ 'accessionNum': sample.accession_number, 'date': sample.date })
    }
  })
  const cardState = {
    accessionNum: selectedAccessionNumbers,
    sampleId: selectedSamples,
    // clearing values on a new sample , initially. Correctly set elsewhere
    // after loading genomics
    selectedAlteration: '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: 0,
    uuid: '',
    mimeType: '',
    multiComparison: true,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
  clearCenterColumn('genomics')
}

watch(patientId, () => {
  loadSamples(patientId.value)
})

onMounted(async () => {
  loadSamples(patientId.value)
})

defineExpose({ loadSamples })
</script>

<style lang="scss" scoped>
.button-disabled {
  pointer-events: none;
  user-select: none;
}

.comparison-section {
  background-color: var(--Grey100);
  padding-top: 16px;
  position: absolute;
  bottom: 0;
  width: calc(100% - 30px);
}

.genomics-disclaimer {
  font-size: 0.7rem;
  color: var(--Grey500);
  bottom: 0;
  left: 0;
}

.genomics-compare-box {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.sample-container {
  position: relative;
}

.samples {
  padding-bottom: 200px;
}

.checkbox-layer {
  position: absolute;
  top: 1rem;
  right: 2rem;
  height: 24px;
  width: 24px;

  &:hover {
    cursor: pointer;
  }
}

.report-only {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--Grey700);
  color: var(--White);
  position: absolute;
  border-radius: 5px;
  font-size: 70%;
  top: 1rem;
  right: 2rem;
  height: 20px;
  width: 35px;
}

.checked {
  color: var(--Black);
}

.unchecked {
  color: var(--Grey500);
}

.item-selected,
.item-selected:hover {
  box-shadow: 3px 3px 10px var(--Grey800);
  transition: all ease-out 200ms;
}
</style>


